/**
 * Laravel Echo and Pusher Integration Library
 * 
 * This library provides a shared user channel subscription system.
 * The user channel is always: App.Models.User.{userId}
 * 
 * Usage in components:
 * 
 * import { useEffect } from 'react';
 * import { subscribeToUserChannel, listenToUserChannelEvent } from 'libs/echo/echo.lib';
 * import userStore from 'stores/user.store';
 * 
 * const MyComponent = () => {
 *   useEffect(() => {
 *     const user = userStore.getUser();
 *     if (user?.id) {
 *       // Subscribe to user channel (shared across all components)
 *       subscribeToUserChannel(user.id);
 *       
 *       // Listen to specific event
 *       const unsubscribe = listenToUserChannelEvent('.ars.summary.report.completed', (data) => {
 *         console.log('Event received:', data);
 *         // Handle event
 *       });
 *       
 *       return () => {
 *         unsubscribe(); // Clean up listener
 *       };
 *     }
 *   }, []);
 * };
 */

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import Cookies from 'js-cookie';
import axiosBarwisInstance from 'libs/axios/axios.lib';

// Initialize Pusher globally
window.Pusher = Pusher;

// Custom authorizer using axios to handle CORS properly
const createCustomAuthorizer = () => {
  return (channel, options) => {
    return {
      authorize: (socketId, callback) => {
        // Use axios instead of default XMLHttpRequest to handle CORS with credentials
        axiosBarwisInstance.post('/broadcasting/auth', {
          socket_id: socketId,
          channel_name: channel.name
        }, {
          headers: {
            'X-XSRF-TOKEN': Cookies.get('XSRF-TOKEN') || '',
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })
        .then(response => {
          callback(null, response.data);
        })
        .catch(error => {
          console.error('Pusher authentication error:', error);
          callback(error, null);
        });
      }
    };
  };
};

// Create Echo instance with custom authorizer
const echoInstance = new Echo({
  broadcaster: 'pusher',
  key: process.env.REACT_APP_PUSHER_KEY,
  cluster: process.env.REACT_APP_PUSHER_CLUSTER,
  forceTLS: true,
  encrypted: true,
  authorizer: createCustomAuthorizer()
});

// Make Echo available globally
window.Echo = echoInstance;

// User channel manager - maintains a single channel instance per user
let currentUserChannel = null;
let currentUserId = null;
const eventListeners = new Map(); // Map of eventName -> Set of callbacks

/**
 * Get the user channel name for a given user ID
 * @param {number|string} userId - The user ID
 * @returns {string} Channel name in format App.Models.User.{userId}
 */
export const getUserChannelName = (userId) => {
  return `App.Models.User.${userId}`;
};

/**
 * Subscribe to the user channel for a given user ID
 * Creates a single shared channel instance that can be used across components
 * @param {number|string} userId - The user ID
 * @returns {object|null} The channel instance or null if userId is invalid
 */
export const subscribeToUserChannel = (userId) => {
  if (!userId) {
    console.warn('subscribeToUserChannel: userId is required');
    return null;
  }

  // If already subscribed to the same user's channel, return existing channel
  if (currentUserChannel && currentUserId === userId) {
    return currentUserChannel;
  }

  // If subscribed to a different user, clean up first
  if (currentUserChannel && currentUserId !== userId) {
    unsubscribeFromUserChannel();
  }

  const channelName = getUserChannelName(userId);
  console.log(`Subscribing to user channel: ${channelName}`);

  // Subscribe to private channel
  const channel = echoInstance.private(channelName);
  currentUserChannel = channel;
  currentUserId = userId;

  // Wait for subscription to succeed
  channel.subscribed(() => {
    console.log(`Successfully subscribed to channel: ${channelName}`);
  });

  // Handle subscription errors
  channel.error((error) => {
    console.error(`Subscription error for channel ${channelName}:`, error);
  });

  return channel;
};

/**
 * Unsubscribe from the current user channel
 */
export const unsubscribeFromUserChannel = () => {
  if (currentUserChannel) {
    const channelName = getUserChannelName(currentUserId);
    console.log(`Unsubscribing from channel: ${channelName}`);
    echoInstance.leave(channelName);
    currentUserChannel = null;
    currentUserId = null;
    eventListeners.clear();
  }
};

/**
 * Listen to a specific event on the user channel
 * Multiple components can listen to the same event
 * @param {string} eventName - The event name (e.g., '.ars.summary.report.completed' or '.' for all events)
 * @param {function} callback - The callback function to execute when event is received
 *   For specific events: callback(data)
 *   For wildcard '.': callback(eventName, data)
 * @returns {function} Unsubscribe function
 */
export const listenToUserChannelEvent = (eventName, callback) => {
  if (!currentUserChannel) {
    console.warn('listenToUserChannelEvent: No active user channel. Call subscribeToUserChannel first.');
    return () => {};
  }

  // Add callback to listeners map
  if (!eventListeners.has(eventName)) {
    eventListeners.set(eventName, new Set());
    // First listener for this event - set up the actual listener
    if (eventName === '.') {
      // Wildcard listener - receives (eventName, data)
      currentUserChannel.listen(eventName, (receivedEventName, data) => {
        const callbacks = eventListeners.get(eventName);
        if (callbacks) {
          callbacks.forEach(cb => {
            try {
              cb(receivedEventName, data);
            } catch (error) {
              console.error(`Error in event listener for ${eventName}:`, error);
            }
          });
        }
      });
    } else {
      // Specific event listener - receives (data)
      currentUserChannel.listen(eventName, (data) => {
        const callbacks = eventListeners.get(eventName);
        if (callbacks) {
          callbacks.forEach(cb => {
            try {
              cb(data);
            } catch (error) {
              console.error(`Error in event listener for ${eventName}:`, error);
            }
          });
        }
      });
    }
  }

  eventListeners.get(eventName).add(callback);

  // Return unsubscribe function
  return () => {
    const callbacks = eventListeners.get(eventName);
    if (callbacks) {
      callbacks.delete(callback);
      // If no more listeners, we could optionally remove the listener
      // But keeping it is fine since it's lightweight
      if (callbacks.size === 0) {
        eventListeners.delete(eventName);
      }
    }
  };
};

/**
 * Get the current user channel instance (if subscribed)
 * @returns {object|null} The current channel instance or null
 */
export const getCurrentUserChannel = () => {
  return currentUserChannel;
};

export default echoInstance;
